import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import MuxUploader, { MuxUploaderDrop, MuxUploaderFileSelect, MuxUploaderProgress, MuxUploaderStatus, } from "@mux/mux-uploader-react";
import VideoAssetWithStreaming from "components/VideoAsset/VideoAssetWithStreaming";
import Modal from "components/Modal";
import LinkButton from "components/LinkButton";
import genericModalStyles from "helpers/genericModalStyles";
import GET_VIDEO_UPLOAD_URL from "queries/getVideoUploadUrl.gql";
import "./style.scss";
const isValidUrl = (value) => {
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    }
    catch (_a) {
        return false;
    }
};
const getCampaignIdFromUrl = () => { var _a, _b; return (_b = (_a = window.location.pathname.match(/\/project\/([^/]+)/)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : ""; };
const isMuxDefault = (value) => value && typeof value === "object" && typeof value.playbackId === "string";
const isPending = (value) => value && typeof value === "object" && value.pending === true;
const VideoUploader = ({ id, name, setValue, defaultValue, onUploadingChange, }) => {
    var _a, _b, _c, _d;
    const existingMux = isMuxDefault(defaultValue) ? defaultValue : null;
    const existingUrl = typeof defaultValue === "string" ? defaultValue : "";
    const videoIsPending = isPending(defaultValue);
    const hasExisting = !!(existingMux || existingUrl);
    const [replacing, setReplacing] = useState(false);
    const [mode, setMode] = useState("upload");
    const [pendingMode, setPendingMode] = useState(null);
    const [urlInput, setUrlInput] = useState(existingUrl);
    const [urlError, setUrlError] = useState(null);
    const [uploadPhase, setUploadPhase] = useState("fetching");
    const [errorMessage, setErrorMessage] = useState(null);
    useEffect(() => {
        if (existingUrl) {
            setValue(name, { videoUrl: existingUrl });
        }
        else if (!hasExisting && !videoIsPending) {
            fetchUploadUrl({ variables: { campaignId: getCampaignIdFromUrl() } });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const [fetchUploadUrl, { data: uploadUrlData, error: queryError }] = useLazyQuery(GET_VIDEO_UPLOAD_URL);
    const uploadUrl = (_b = (_a = uploadUrlData === null || uploadUrlData === void 0 ? void 0 : uploadUrlData.videoUploadUrl) === null || _a === void 0 ? void 0 : _a.url) !== null && _b !== void 0 ? _b : null;
    const uploadId = (_d = (_c = uploadUrlData === null || uploadUrlData === void 0 ? void 0 : uploadUrlData.videoUploadUrl) === null || _c === void 0 ? void 0 : _c.uploadId) !== null && _d !== void 0 ? _d : null;
    useEffect(() => {
        if (uploadUrlData) {
            setUploadPhase("uploading");
        }
    }, [uploadUrlData]);
    useEffect(() => {
        if (queryError) {
            setUploadPhase("error");
            setErrorMessage("Failed to get upload URL. Please try again.");
        }
    }, [queryError]);
    useEffect(() => {
        onUploadingChange === null || onUploadingChange === void 0 ? void 0 : onUploadingChange(uploadPhase === "uploading");
    }, [uploadPhase, onUploadingChange]);
    const handleReplace = () => {
        setReplacing(true);
        setValue(name, {});
        setUploadPhase("fetching");
        fetchUploadUrl({ variables: { campaignId: getCampaignIdFromUrl() } });
    };
    const handleCancelReplace = () => {
        setReplacing(false);
        setMode("url");
        setUploadPhase("idle");
        setErrorMessage(null);
        // Restore the original value
        if (existingUrl) {
            setValue(name, { videoUrl: existingUrl });
        }
        else {
            setValue(name, {});
        }
    };
    const hasDataToLose = (targetMode) => {
        if (targetMode === "upload" && urlInput && urlInput !== existingUrl) {
            return true;
        }
        if (targetMode === "url" && (uploadPhase === "uploading" || uploadPhase === "processing")) {
            return true;
        }
        return false;
    };
    const applyModeChange = (newMode) => {
        setMode(newMode);
        setErrorMessage(null);
        if (newMode === "upload") {
            setUrlInput("");
            setValue(name, {});
            if (uploadUrl) {
                setUploadPhase("uploading");
            }
            else {
                setUploadPhase("fetching");
                fetchUploadUrl({ variables: { campaignId: getCampaignIdFromUrl() } });
            }
        }
        else {
            setUploadPhase("idle");
            setValue(name, {});
        }
    };
    const handleModeChange = (newMode) => {
        if (newMode === mode) {
            return;
        }
        if (hasDataToLose(newMode)) {
            setPendingMode(newMode);
        }
        else {
            applyModeChange(newMode);
        }
    };
    const handleConfirmModeChange = () => {
        if (pendingMode) {
            applyModeChange(pendingMode);
        }
        setPendingMode(null);
    };
    const handleCancelModeChange = () => {
        setPendingMode(null);
    };
    const handleUrlChange = (e) => {
        const { value } = e.target;
        setUrlInput(value);
        setUrlError(null);
        setValue(name, { videoUrl: value });
    };
    const handleUrlBlur = () => {
        if (urlInput && !isValidUrl(urlInput)) {
            setUrlError("Please enter a valid URL, e.g. https://www.youtube.com/watch?v=...");
            setValue(name, { videoUrl: "" });
        }
    };
    const handleUploadSuccess = () => {
        setValue(name, { uploadId });
        setUploadPhase("processing");
    };
    const handleUploadError = () => {
        setUploadPhase("error");
        setErrorMessage("Upload failed. Please try again.");
    };
    const handleRetry = () => {
        setErrorMessage(null);
        setUploadPhase("fetching");
        fetchUploadUrl({ variables: { campaignId: getCampaignIdFromUrl() } });
    };
    // ── Pending Mux video ─────────────────────────────────────────────────────
    if (videoIsPending) {
        return (_jsx("div", { className: "video-uploader", id: id, children: _jsxs("div", { className: "video-uploader__processing", children: [_jsx("p", { className: "video-uploader__processing-title", children: "Video is still processing." }), _jsx("p", { className: "video-uploader__processing-detail", children: "This video is still being processed and will appear on your campaign once ready. You can save other changes to this update in the meantime." })] }) }));
    }
    // ── Edit mode: show existing video with a Replace button ──────────────────
    if (hasExisting && !replacing) {
        return (_jsxs("div", { className: "video-uploader", id: id, children: [existingMux && _jsx(VideoAssetWithStreaming, { update: existingMux, classPrefix: "video-asset" }), existingUrl && !existingMux && (_jsx(VideoAssetWithStreaming, { update: { assets: [{ publicUrl: existingUrl, fileType: "" }] }, classPrefix: "video-asset" })), _jsx("button", { type: "button", className: "video-uploader__replace-btn", onClick: handleReplace, children: "Replace video" })] }));
    }
    // ── Create mode (or after Replace clicked): show tabs ────────────────────
    return (_jsxs("div", { className: "video-uploader", id: id, children: [_jsxs("div", { className: "video-uploader__tabs", role: "group", children: [_jsx("button", { type: "button", className: `video-uploader__tab${mode === "upload" ? " --active" : ""}`, onClick: () => handleModeChange("upload"), children: "Upload a file" }), _jsx("button", { type: "button", className: `video-uploader__tab${mode === "url" ? " --active" : ""}`, onClick: () => handleModeChange("url"), children: "YouTube / Vimeo URL" })] }), mode === "url" && (_jsxs("div", { className: "video-uploader__url-mode", children: [_jsx("input", { type: "text", className: `video-uploader__url-input${urlError ? " --error" : ""}`, placeholder: "https://www.youtube.com/watch?v=...", value: urlInput, onChange: handleUrlChange, onBlur: handleUrlBlur }), urlError ? (_jsx("p", { className: "video-uploader__url-error", children: urlError })) : (_jsx("p", { className: "video-uploader__hint", children: "Upload your video to YouTube or Vimeo and paste the link above." }))] })), mode === "upload" && (_jsxs("div", { className: "video-uploader__upload-mode", children: [uploadPhase === "fetching" && (_jsx("div", { className: "video-uploader__loading", children: "Preparing upload\u2026" })), uploadPhase === "uploading" && uploadUrl && (_jsxs("div", { className: "video-uploader__mux-wrapper", children: [_jsx(MuxUploader, { id: "video-uploader-mux", className: "video-uploader__mux", endpoint: uploadUrl, onSuccess: handleUploadSuccess, onError: handleUploadError, noRetry: true, noDrop: true, style: { display: "none" } }), _jsxs(MuxUploaderDrop, { className: "video-uploader__mux-drop", muxUploader: "video-uploader-mux", overlay: true, children: [_jsx("span", { slot: "heading", children: "Drop a video file here to upload" }), _jsx(MuxUploaderFileSelect, { className: "video-uploader__mux-file-select", muxUploader: "video-uploader-mux" }), _jsx(MuxUploaderStatus, { className: "video-uploader__mux-status", muxUploader: "video-uploader-mux" }), _jsx(MuxUploaderProgress, { className: "video-uploader__mux-progress", muxUploader: "video-uploader-mux", type: "bar" })] })] })), uploadPhase === "processing" && (_jsxs("div", { className: "video-uploader__processing", children: [_jsx("p", { className: "video-uploader__processing-title", children: "Video uploaded \u2014 processing, this may take a few minutes." }), _jsxs("p", { className: "video-uploader__processing-detail", children: ["You can post your update now and we will notify you once it has been processed and posted to your campaign.", " ", !replacing && (_jsx("span", { children: "If you choose to notify donors, those emails will not be sent until the update is ready to be viewed." }))] })] })), uploadPhase === "error" && (_jsxs("div", { className: "video-uploader__error", children: [_jsx("p", { children: errorMessage || (queryError && queryError.message) || "An error occurred." }), _jsx("button", { type: "button", className: "video-uploader__button", onClick: handleRetry, children: "Try again" })] }))] })), _jsxs(Modal, { isOpen: !!pendingMode, contentLabel: "Confirm video type change", customStyle: genericModalStyles, shouldCloseOnOverlayClick: true, onRequestClose: handleCancelModeChange, children: [_jsx("div", { className: "video-uploader__confirm-heading", children: "Change video type" }), _jsx("p", { className: "video-uploader__confirm-message", children: "If you change video type, your current details will be cleared. Do you want to proceed?" }), _jsxs("div", { className: "video-uploader__confirm-actions", children: [_jsx(LinkButton, { size: "small", isBold: true, look: "primary", onClick: handleConfirmModeChange, children: "Proceed" }), _jsx(LinkButton, { size: "small", isBold: true, look: "secondary-outline", onClick: handleCancelModeChange, children: "Cancel" })] })] }), hasExisting && (_jsx("button", { type: "button", className: "video-uploader__cancel-btn", onClick: handleCancelReplace, children: "Cancel and restore original video" }))] }));
};
export default VideoUploader;
